import type { ReactNode } from 'react'
import { toMessage } from '@/api/problem'
import { cn } from '@/utils/cn'
import { InboxIcon, RefreshIcon, SpinnerIcon, WarningIcon } from '@/components/icons'
import { Button } from './Button'


/**
 * loading / empty / error 三态的标准呈现。
 *
 * AGENTS.md 6 要求每个页面级组件都处理这三种状态。集中在这里的好处不只是省
 * 代码：它保证了三态的**文案语气与视觉重量一致**，不会出现一个页面用红色大
 * 警告、另一个页面只在角落显示一行小灰字。
 */

/** 骨架块。`shimmer` 的扫光在 styles/index.css 里。 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('shimmer relative overflow-hidden rounded-md bg-surface-muted', className)}
      aria-hidden="true"
    />
  )
}

/** 页面级加载态。给 role="status" 让读屏器知道「正在加载」。 */
export function LoadingState({
  label = '正在加载…',
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground',
        className,
      )}
    >
      <SpinnerIcon className="size-6 animate-spin text-primary" />
      <p className="text-sm">{label}</p>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-panel border border-dashed border-border px-6 py-16 text-center',
        className,
      )}
    >
      <span className="text-border-strong">{icon ?? <InboxIcon className="size-9" />}</span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && (
          <p className="mx-auto max-w-md text-sm leading-6 text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  )
}

/**
 * 页面级错误态。
 *
 * 展示 `request_id`（后端 Problem 的 `instance`）是有意的：它是把用户看到的
 * 错误关联到服务端日志的唯一线索，出问题时用户能直接把它报上来。
 */
export function ErrorState({
  error,
  onRetry,
  title = '加载失败',
  className,
}: {
  error: unknown
  onRetry?: () => void
  title?: string
  className?: string
}) {
  const message = toMessage(error)

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-panel border border-danger/30 bg-danger/5 px-6 py-14 text-center',
        className,
      )}
    >
      <WarningIcon className="size-9 text-danger" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="mx-auto max-w-md text-sm leading-6 text-muted-foreground">{message}</p>
      </div>
      {onRetry && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRetry}
          leadingIcon={<RefreshIcon className="size-4" />}
        >
          重试
        </Button>
      )}
    </div>
  )
}

/**
 * 三态调度器 —— 直接吃 TanStack Query 的返回值。
 *
 * 把「先判错误、再判加载、再判空、最后渲染」这段每页都一样的顺序收敛成一处。
 * 顺序是有讲究的：
 *
 *   - **error 要在 empty 之前判。** 否则请求失败会被显示成「暂无内容」，
 *     那是两件完全不同的事，用户的下一步动作也不同。
 *   - **只在没有旧数据时才显示错误页。** 翻页失败时如果把已经渲染好的一页
 *     换成错误页，用户会觉得数据丢了；保留旧内容更诚实（TanStack 的
 *     `placeholderData` 已经把上一页留着了）。
 */
export interface QueryBoundaryProps<T> {
  /** `useQuery(...)` 的返回值。只取需要的几个字段，方便测试时手搓假对象。 */
  query: {
    data: T | undefined
    error: unknown
    isPending: boolean
    isError: boolean
    refetch?: () => void
  }
  /** 判空规则由调用方给：「空」对列表和对详情不是一回事。 */
  isEmpty?: (data: T) => boolean
  /** 自定义加载态，通常传骨架屏以避免布局跳动。 */
  loading?: ReactNode
  empty?: ReactNode
  children: (data: T) => ReactNode
}

export function QueryBoundary<T>({
  query,
  isEmpty,
  loading,
  empty,
  children,
}: QueryBoundaryProps<T>) {
  const { data, error, isPending, isError, refetch } = query

  if (isError && data === undefined) {
    return <ErrorState error={error} {...(refetch ? { onRetry: refetch } : {})} />
  }
  if (data === undefined) {
    if (!isPending) return null
    return <>{loading ?? <LoadingState />}</>
  }
  if (isEmpty?.(data)) {
    return <>{empty ?? <EmptyState title="暂无内容" />}</>
  }
  return <>{children(data)}</>
}
